import { ratingSummaryDataGrid } from '@components/dataGrid/DataGrid.styles';
import { DataGrid } from '@components/dataGrid/Datagrid';
import { Dialog } from '@components/dialog';
import { ToastType } from '@components/toast/ToastType.enum';

import { Box, Divider, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { shallow } from 'zustand/shallow';
import {
  RatingModalType,
  RatingModalTypes,
  RatingSummaryDataGridItem,
  RatingSummaryDataGridItemType,
  marginLeft,
  marginTop,
} from '@pages/QuoteDetails/sidebarItems/summary/modals/RatingSummaryModal/RatingSummaryModal.constants';
import Loading from '@components/Loading';

import { useSearchQuoteItemCoverages } from '@services/quoteItem';
import { searchLastSuccessfulRatingParams } from '@services/quoteRating/quoteRating.helpers';
import { useSearchQuoteRatings } from '@services/quoteRating/useSearchQuoteRatings';
import { useQuote } from '@services/quotes';
import { formatCurrency } from '@utils/number';
import { useStore } from '@store/GlobalStore';
import { groupBy } from 'lodash';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { RatingReportJsonViewer } from '@pages/QuoteDetails/sidebarItems/summary/modals/RatingSummaryModal/RatingReportJsonViewer';
import { EmptyColumnHeaders } from '@pages/QuoteDetails/sidebarItems/summary/modals/utils';
import { useQuoteStore } from '@store/Quote/QuoteStore';
import { QuoteRating } from '@models/quoteRating';
import { QuoteType } from '@models/quotes';

type RatingSummaryModalProps = {
  quoteId: string;
  isOpen: boolean;
  onCloseModal: () => void;
  ratingResult?: QuoteRating; // Optional rating result to use instead of loading latest
};

const basicHeaderSx = {
  color: 'info.hint',
  fontWeight: 'bold',
  textTransform: 'uppercase',
};

const quoteTypesWithAlwaysVisiblePolicyTermDetails = [QuoteType.NewBusiness, QuoteType.Renewal, QuoteType.Rewrite];

function formatFactor({
  factor,
  precision,
  applyTermFactor,
}: {
  factor: number | null | undefined;
  precision: number;
  applyTermFactor: boolean;
}) {
  if (!applyTermFactor || factor === null || factor === undefined) return undefined;
  if (factor === 1) return '1';
  return factor.toFixed(precision);
}

function getDataGridColumns({
  isAnnualTerm,
  hasPolicyTermPremium,
  isEndorsement,
}: {
  isAnnualTerm: boolean;
  hasPolicyTermPremium: boolean;
  isEndorsement: boolean;
}): GridColDef[] {
  const columns: (GridColDef & { isVisible: boolean })[] = [
    {
      field: 'coverage',
      headerName: '',
      flex: 3,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const fontWeight = params.row.type === RatingSummaryDataGridItemType.PrimaryTitle ? 'bold' : 'normal';
        return (
          <Typography
            variant='body2'
            sx={{
              fontWeight,
              color: 'info.hint',
              marginTop: marginTop[params.row.type as RatingSummaryDataGridItemType],
              marginLeft: marginLeft[params.row.type as RatingSummaryDataGridItemType],
              display: 'block',
              my: 0,
            }}
          >
            {params.row.title ?? '-'}
          </Typography>
        );
      },
      isVisible: isAnnualTerm,
    },
    // version of coverage for the non annual views
    {
      field: 'coverage',
      headerName: '',
      flex: 3,
      sortable: false,
      editable: false,
      renderCell: (params: GridRenderCellParams) => {
        const fontWeight = params.row.type === RatingSummaryDataGridItemType.PrimaryTitle ? 'bold' : 'normal';
        return (
          <Typography
            variant='body2'
            sx={{
              fontWeight,
              color: 'info.hint',
              marginTop: marginTop[params.row.type as RatingSummaryDataGridItemType],
              marginLeft: marginLeft[params.row.type as RatingSummaryDataGridItemType],
              display: 'block',
              my: 0,
            }}
          >
            {params.row.title ?? '-'}
          </Typography>
        );
      },
      isVisible: !isAnnualTerm,
    },
    {
      headerAlign: 'right',
      flex: 1,
      sortable: false,
      align: 'right',
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.type !== RatingSummaryDataGridItemType.Default) return '';
        return (
          <Typography
            variant='body2'
            sx={{
              color: 'info.hint',
              fontWeight: 'bold',
              marginRight: '16px',
            }}
          >
            {params.row.ratedPremium ?? '-'}
          </Typography>
        );
      },
      field: 'ratedPremium',
      headerName: '',
      isVisible: isAnnualTerm && !isEndorsement,
    },
    // version of coverage for the non annual views
    {
      renderHeader: () => (
        <Typography variant='caption' sx={basicHeaderSx}>
          Annualized
        </Typography>
      ),
      headerAlign: 'right',
      flex: 1,
      sortable: false,
      align: 'right',
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.type !== RatingSummaryDataGridItemType.Default) return '';
        return (
          <Typography
            variant='body2'
            sx={{
              color: 'info.hint',
              fontWeight: 'bold',
            }}
          >
            {params.row.ratedPremium ?? '-'}
          </Typography>
        );
      },
      editable: false,
      field: 'ratedPremium',
      headerName: '',
      isVisible: !isAnnualTerm,
    },
    {
      field: 'termFactor',
      renderHeader: () => (
        <Typography variant='caption' sx={basicHeaderSx}>
          Term Factor
        </Typography>
      ),
      headerAlign: 'right',
      flex: 1,
      editable: false,
      sortable: false,
      align: 'right',
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.type !== RatingSummaryDataGridItemType.Default) return '';
        return (
          <Typography
            variant='body2'
            sx={{
              color: 'info.hint',
              fontWeight: 'normal',
            }}
          >
            {params.row.termFactor ?? '-'}
          </Typography>
        );
      },
      isVisible: !isAnnualTerm && !hasPolicyTermPremium && !isEndorsement,
    },
    {
      field: 'termPremium',
      renderHeader: () => (
        <Typography
          variant='caption'
          sx={{
            ...basicHeaderSx,
            marginRight: '16px',
          }}
        >
          Term
        </Typography>
      ),
      headerAlign: 'right',
      flex: 1,
      editable: false,
      sortable: false,
      align: 'right',
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.type !== RatingSummaryDataGridItemType.Default) return '';
        return (
          <Typography
            variant='body2'
            sx={{
              color: 'info.hint',
              fontWeight: 'bold',
              marginRight: '16px',
            }}
          >
            {params.row.termPremium ?? '-'}
          </Typography>
        );
      },
      isVisible: !isAnnualTerm && !hasPolicyTermPremium && !isEndorsement,
    },
    {
      field: 'policyTermFactor',
      renderHeader: () => (
        <Typography variant='caption' sx={basicHeaderSx}>
          Policy Term Factor
        </Typography>
      ),
      headerAlign: 'right',
      flex: 1,
      editable: false,
      sortable: false,
      align: 'right',
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.type !== RatingSummaryDataGridItemType.Default) return '';
        return (
          <Typography
            variant='body2'
            sx={{
              color: 'info.hint',
              fontWeight: 'normal',
            }}
          >
            {params.row.policyTermFactor ?? '-'}
          </Typography>
        );
      },
      isVisible: !isAnnualTerm && hasPolicyTermPremium && !isEndorsement,
    },
    {
      field: 'policyTermPremium',
      renderHeader: () => (
        <Typography
          variant='caption'
          sx={{
            ...basicHeaderSx,
            marginRight: '16px',
          }}
        >
          Policy Term Premium
        </Typography>
      ),
      headerAlign: 'right',
      flex: 1,
      editable: false,
      sortable: false,
      align: 'right',
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.type !== RatingSummaryDataGridItemType.Default) return '';
        return (
          <Typography
            variant='body2'
            sx={{
              color: 'info.hint',
              fontWeight: 'bold',
              marginRight: '16px',
            }}
          >
            {params.row.policyTermPremium ?? '-'}
          </Typography>
        );
      },
      isVisible: !isAnnualTerm && hasPolicyTermPremium && !isEndorsement,
    },
    {
      field: 'policyTermFactor',
      renderHeader: () => (
        <Typography
          variant='caption'
          sx={{
            ...basicHeaderSx,
            wordWrap: 'break-word',
            whiteSpace: 'normal',
          }}
        >
          Policy Term Factor
        </Typography>
      ),
      headerAlign: 'right',
      flex: 1,
      editable: false,
      sortable: false,
      align: 'right',
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.type !== RatingSummaryDataGridItemType.Default) return '';
        return (
          <Typography
            variant='body2'
            sx={{
              color: 'info.hint',
              fontWeight: 'normal',
            }}
          >
            {params.row.policyTermFactor ?? '-'}
          </Typography>
        );
      },
      isVisible: !isAnnualTerm && hasPolicyTermPremium && isEndorsement,
    },
    {
      field: 'policyTermPremium',
      renderHeader: () => (
        <Typography
          variant='caption'
          sx={{
            ...basicHeaderSx,
            whiteSpace: 'normal',
          }}
        >
          Policy Term Premium
        </Typography>
      ),
      headerAlign: 'right',
      flex: 1,
      editable: false,
      sortable: false,
      align: 'right',
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.type !== RatingSummaryDataGridItemType.Default) return '';
        return (
          <Typography
            variant='body2'
            sx={{
              color: 'info.hint',
              fontWeight: 'bold',
              marginRight: '16px',
            }}
          >
            {params.row.policyTermPremium ?? '-'}
          </Typography>
        );
      },
      isVisible: !isAnnualTerm && hasPolicyTermPremium && isEndorsement,
    },
    {
      field: 'transactionTermFactor',
      renderHeader: () => (
        <Typography
          variant='caption'
          sx={{
            ...basicHeaderSx,
            wordWrap: 'break-word',
            whiteSpace: 'normal',
          }}
        >
          End. Term Factor
        </Typography>
      ),
      headerAlign: 'right',
      flex: 1,
      editable: false,
      sortable: false,
      align: 'right',
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.type !== RatingSummaryDataGridItemType.Default) return '';
        return (
          <Typography
            variant='body2'
            sx={{
              color: 'info.hint',
              fontWeight: 'normal',
            }}
          >
            {params.row.transactionTermFactor ?? '-'}
          </Typography>
        );
      },
      isVisible: !isAnnualTerm && isEndorsement,
    },
    {
      field: 'transactionTermPremium',
      renderHeader: () => (
        <Typography
          variant='caption'
          sx={{
            ...basicHeaderSx,
            wordWrap: 'break-word',
            whiteSpace: 'normal',
            marginRight: '16px',
          }}
        >
          End. Term Premium
        </Typography>
      ),
      headerAlign: 'right',
      flex: 1,
      editable: false,
      sortable: false,
      align: 'right',
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.type !== RatingSummaryDataGridItemType.Default) return '';
        return (
          <Typography
            variant='body2'
            sx={{
              color: 'info.hint',
              fontWeight: 'bold',
              marginRight: '16px',
            }}
          >
            {params.row.transactionTermPremium ?? '-'}
          </Typography>
        );
      },
      isVisible: !isAnnualTerm && isEndorsement,
    },
  ];

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return columns.filter((column) => column.isVisible).map(({ isVisible, ...rest }) => rest);
}

const annualTermDataGridColumns: GridColDef[] = getDataGridColumns({
  isAnnualTerm: true,
  hasPolicyTermPremium: false,
  isEndorsement: false,
});

// standard for legacy quotes without policyTermPremium in the coverages
const proRataGridColumnsBeforePolicyTerm: GridColDef[] = getDataGridColumns({
  isAnnualTerm: false,
  hasPolicyTermPremium: false,
  isEndorsement: true,
});

// standard for new business when policyTermPremium will have a value in the coverages
const proRataGridColumns: GridColDef[] = getDataGridColumns({
  isAnnualTerm: false,
  hasPolicyTermPremium: true,
  isEndorsement: false,
});

// standard for endorsement quotes when policyTermPremium will have a value in the coverages
const proRataGridColumnsEndorsement: GridColDef[] = getDataGridColumns({
  isAnnualTerm: false,
  hasPolicyTermPremium: true,
  isEndorsement: true,
});

export function RatingSummaryModal({ quoteId, isOpen, onCloseModal, ratingResult }: RatingSummaryModalProps) {
  const { quote, isLoading: isLoadingQuote } = useQuote(quoteId);

  const [structuredQuote] = useQuoteStore((state) => [state.structuredQuote], shallow);

  const [modalType, setModalType] = useState<RatingModalType>(RatingModalTypes.PremiumDetails);
  const [ratingReport, setRatingReport] = useState<string>();
  const { enqueueSnackbar } = useSnackbar();
  const { isInternalUser } = useStore((state) => state.userAuth, shallow);

  const { data: quoteItemCoveragesResult, isLoading: isLoadingQuoteItemCoverages } = useSearchQuoteItemCoverages({
    quoteId,
  });
  const { data: latestRatingResults } = useSearchQuoteRatings({
    quoteId,
    enabled: !ratingResult, // Only fetch if no rating result is provided
    params: searchLastSuccessfulRatingParams,
  });

  const lastSuccessfulRating = ratingResult || latestRatingResults?.pages[0]?.items[0];

  const [rows, isAnnualTerm, taxFeeSurchargeCoverages] = useMemo(() => {
    const quoteItemCoverages =
      isLoadingQuote || isLoadingQuoteItemCoverages ? [] : (quoteItemCoveragesResult?.items ?? []);
    const quoteItemsByType = groupBy(structuredQuote?.quoteItems, (qi) => qi.quoteItemType.id);
    const coveragesToShow = quoteItemCoverages.flatMap((qic) => {
      if (!qic.isIncludedInPremium || qic.coverage.rollupCategory !== 'Premium') return [];
      const quoteItem = structuredQuote?.quoteItems.find((qi) => qi.id === qic.quoteItemId);
      if (!quoteItem) return [];
      const customLabel =
        quoteItem.attributeTypes.GLClassificationName?.attribute?.value ??
        quoteItem.attributeTypes.CPPropertyLabel?.attribute?.value;
      const label = quoteItem.quoteItemType.label;
      const labelCountSuffix =
        quoteItemsByType[quoteItem?.quoteItemTypeId].length > 1 ? ` (${quoteItem?.instanceNumber})` : '';
      const labelCustomSuffix = customLabel ? ` - ${customLabel}` : '';

      return {
        id: qic.id,
        insuranceLineName: qic.coverage.insuranceLine.name,
        quoteItemLabel: `${label}${labelCountSuffix}${labelCustomSuffix}`.trim(),
        label: qic.coverage.coverageName,
        termPremium: qic.quoteItemCoverageAttributes.find(
          (qica) => qica.coverageAttributeType.coverageAttributeCategory === 'TermPremium',
        )?.value,
        policyTermPremium: qic.quoteItemCoverageAttributes.find(
          (qica) => qica.coverageAttributeType.coverageAttributeCategory === 'PolicyTermPremium',
        )?.value,
        ratedPremium: qic.quoteItemCoverageAttributes.find(
          (qica) => qica.coverageAttributeType.coverageAttributeCategory === 'RatedPremium',
        )?.value,
        policyTermFactor: qic.policyTermFactor,
        termFactor: qic.termFactor,
        transactionTermFactor: qic.transactionTermFactor,
        applyTermFactor: qic.coverage.applyTermFactor,
        transactionTermPremium: qic.quoteItemCoverageAttributes.find(
          (qica) => qica.coverageAttributeType.coverageAttributeCategory === 'TermPremium',
        )?.value,
      };
    });

    // Extract TaxFeeSurcharge coverages
    const taxFeeSurchargeCoverages = quoteItemCoverages
      .filter((qic) => qic.isIncludedInPremium && qic.coverage.rollupCategory === 'TaxFeeSurcharge')
      .map((qic) => ({
        id: qic.id,
        label: qic.coverage.coverageName,
        termPremium: qic.quoteItemCoverageAttributes.find(
          (qica) => qica.coverageAttributeType.coverageAttributeCategory === 'TermPremium',
        )?.value as number | undefined,
        policyTermPremium: qic.quoteItemCoverageAttributes.find(
          (qica) => qica.coverageAttributeType.coverageAttributeCategory === 'PolicyTermPremium',
        )?.value as number | undefined,
        ratedPremium: qic.quoteItemCoverageAttributes.find(
          (qica) => qica.coverageAttributeType.coverageAttributeCategory === 'RatedPremium',
        )?.value as number | undefined,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const headerSeparator = '-|-';
    const coveragesByHeader = groupBy(
      coveragesToShow,
      (record) => `${record.insuranceLineName}${headerSeparator}${record.quoteItemLabel}`,
    );
    let lastHeader1: string | undefined;
    let lastHeader2: string | undefined;
    const sortedHeaders = Object.keys(coveragesByHeader).sort();
    const rows: RatingSummaryDataGridItem[] = sortedHeaders.flatMap((headerSet) => {
      const coverages = coveragesByHeader[headerSet];
      const [currentHeader1, currentHeader2] = headerSet.split(headerSeparator);
      const headers = [];
      if (currentHeader1 !== lastHeader1) {
        headers.push({
          id: `id-${currentHeader1}`,
          type: RatingSummaryDataGridItemType.PrimaryTitle,
          title: currentHeader1,
        });
        lastHeader1 = currentHeader1;
        lastHeader2 = undefined;
      }
      if (currentHeader2 !== lastHeader2) {
        headers.push({
          id: `id-${headerSet}`,
          type: RatingSummaryDataGridItemType.SecondaryTitle,
          title: currentHeader2,
        });
        lastHeader2 = currentHeader2;
      }
      return [
        ...headers,
        ...coverages
          .map((coverage) => ({
            id: coverage.id,
            type: RatingSummaryDataGridItemType.Default,
            title: coverage.label,
            termPremium: formatCurrency((coverage.termPremium as number | undefined) ?? 0),
            policyTermPremium: formatCurrency((coverage.policyTermPremium as number | undefined) ?? 0),
            ratedPremium: formatCurrency((coverage.ratedPremium as number | undefined) ?? 0),
            policyTermFactor: formatFactor({
              factor: coverage.policyTermFactor,
              precision: 3,
              applyTermFactor: coverage.applyTermFactor,
            }),
            termFactor:
              coverage.applyTermFactor && coverage.termFactor && coverage.termFactor !== 1
                ? `${coverage.termFactor}`
                : undefined,
            transactionTermFactor: formatFactor({
              factor: coverage.transactionTermFactor,
              precision: 4,
              applyTermFactor: coverage.applyTermFactor,
            }),
            transactionTermPremium: formatCurrency((coverage.transactionTermPremium as number | undefined) ?? 0),
          }))
          .sort((a, b) => a.title.localeCompare(b.title)),
      ];
    });
    const hasPolicyTermPremium = (quote?.rateVersion ?? structuredQuote?.rateVersion ?? 0) >= 46;
    const rateVersion = quote?.rateVersion ?? structuredQuote?.rateVersion;
    const isAnnualTerm = coveragesToShow.every((coverage) => {
      const olderQuoteWithFullTermFactor =
        !hasPolicyTermPremium && (coverage.termFactor === null || coverage.termFactor === 1);
      const isFullPolicyTerm =
        (rateVersion ?? 0) >= 46 && coverage.transactionTermFactor === 1 && coverage.policyTermFactor === 1;

      return olderQuoteWithFullTermFactor || isFullPolicyTerm;
    });
    return [rows, isAnnualTerm, taxFeeSurchargeCoverages];
  }, [quoteItemCoveragesResult, structuredQuote, isLoadingQuoteItemCoverages, isLoadingQuote, quote?.rateVersion]);

  const switchModalType = () => {
    if (modalType === RatingModalTypes.PremiumDetails) {
      setModalType(RatingModalTypes.RatingReport);
    } else {
      setModalType(RatingModalTypes.PremiumDetails);
    }
  };

  const tertiaryButtonTxt =
    modalType === RatingModalTypes.PremiumDetails ? 'View Rating Report' : 'View Premium Details';

  useEffect(() => {
    if (lastSuccessfulRating && modalType === RatingModalTypes.RatingReport && !ratingReport) {
      getRatingReport();
    }
    // eslint-disable-next-line
  }, [lastSuccessfulRating, modalType, ratingReport]);

  const getRatingReport = async () => {
    if (!lastSuccessfulRating?.ratingDetailsFileUrl) return;
    fetch(lastSuccessfulRating.ratingDetailsFileUrl)
      .then(async (response) => {
        const res = await response.json();
        if (res) {
          setRatingReport(res);
        }
      })
      .catch((_e) => {
        enqueueSnackbar('Fetching Rating Report failed', {
          variant: ToastType.Error,
          persist: true,
        });
      });
  };

  let headerText =
    modalType === RatingModalTypes.PremiumDetails
      ? `Premium Details for Quote #${quote?.policyNumber?.policyNumber}`
      : `Rating Report for #${quote?.policyNumber?.policyNumber}`;

  if (ratingResult) {
    headerText += ` | RV #${ratingResult?.rateVersion}`;
  }

  const isEndorsement =
    structuredQuote?.quoteType === QuoteType.Endorsement || quote?.quoteType === QuoteType.Endorsement;
  const quoteType = structuredQuote?.quoteType ?? quote?.quoteType;
  const hasPolicyTermPremium = (quote?.rateVersion ?? structuredQuote?.rateVersion ?? 0) >= 46;
  const shouldAlwaysShowTermDetails =
    quoteType === QuoteType.Endorsement ||
    (hasPolicyTermPremium && quoteTypesWithAlwaysVisiblePolicyTermDetails.includes(quoteType as QuoteType));
  const showDetailedTermColumns = !isAnnualTerm || shouldAlwaysShowTermDetails;

  let columns: GridColDef[] = annualTermDataGridColumns;
  if (showDetailedTermColumns) {
    if (hasPolicyTermPremium) {
      if (isEndorsement) {
        columns = proRataGridColumnsEndorsement;
      } else {
        columns = proRataGridColumns;
      }
    } else {
      columns = proRataGridColumnsBeforePolicyTerm;
    }
  }

  const renderFooterContent = () => {
    const rowStyles = {
      display: 'flex',
      width: '100%',
      alignItems: 'flex-start',
    };

    // Match the DataGrid's marginRight on the last value column
    let annualizedMr = showDetailedTermColumns ? (isEndorsement ? '220px' : '-10px') : '26px';
    const termMr = '26px';
    let spacerColumnMr = '-36px';
    // if not policyTermPremium then need to adjust slightly for endorsement quotes to account for the missing policy term premium column
    if (isEndorsement && !hasPolicyTermPremium) {
      annualizedMr = '26px';
    }
    if (isEndorsement && hasPolicyTermPremium) {
      annualizedMr = '26px';
      spacerColumnMr = '194px';
    }

    return (
      <Box sx={{ maxWidth: '100%' }}>
        <Divider sx={{ marginBottom: '12px' }} />
        <Box sx={{ padding: '8px 0' }}>
          {/* Premium Total Row */}
          <Box sx={rowStyles}>
            <Box sx={{ flex: 3 }}>
              <Typography
                variant='body2'
                sx={{
                  color: 'info.hint',
                  ml: 3,
                  fontWeight: 'bold',
                }}
              >
                Premium Total
              </Typography>
            </Box>
            <Box sx={{ flex: 1, textAlign: 'right', mr: annualizedMr }}>
              <Typography
                variant='body2'
                sx={{
                  color: 'info.hint',
                  fontWeight: 'bold',
                }}
              >
                {formatCurrency(lastSuccessfulRating?.totalPremium)}
              </Typography>
            </Box>
            {showDetailedTermColumns && <Box sx={{ flex: 1, mr: spacerColumnMr }} />}
            {showDetailedTermColumns && (
              <Box sx={{ flex: 1, textAlign: 'right', mr: termMr }}>
                <Typography
                  variant='body2'
                  sx={{
                    color: 'info.hint',
                    fontWeight: 'bold',
                  }}
                >
                  {formatCurrency(lastSuccessfulRating?.termPremium)}
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={rowStyles}>
            <Box sx={{ flex: 3 }}>
              <Typography
                variant='body2'
                sx={{
                  color: 'info.hint',
                  ml: 3,
                  fontWeight: 'bold',
                }}
              >
                Taxes, Fees, Surcharges
              </Typography>
            </Box>

            {taxFeeSurchargeCoverages.length === 0 ? (
              <>
                <Box sx={{ flex: 1, textAlign: 'right', mr: annualizedMr }}>
                  <Typography
                    variant='body2'
                    sx={{
                      color: 'info.hint',
                      fontWeight: 'bold',
                    }}
                  >
                    {formatCurrency(lastSuccessfulRating?.totalTaxFeeSurcharge)}
                  </Typography>
                </Box>
                {showDetailedTermColumns && <Box sx={{ flex: 1, mr: spacerColumnMr }} />}
                {showDetailedTermColumns && (
                  <Box sx={{ flex: 1, textAlign: 'right', mr: termMr }}>
                    <Typography
                      variant='body2'
                      sx={{
                        color: 'info.hint',
                        fontWeight: 'bold',
                      }}
                    >
                      {formatCurrency(lastSuccessfulRating?.termTaxFeeSurcharge)}
                    </Typography>
                  </Box>
                )}
              </>
            ) : null}
          </Box>

          {/* Tax/Fee/Surcharge Items */}
          {taxFeeSurchargeCoverages.map((coverage) => (
            <Box key={coverage.id} sx={{ ...rowStyles, py: 0.25 }}>
              <Box sx={{ flex: 3 }}>
                <Typography
                  variant='body2'
                  sx={{
                    color: 'info.hint',
                    ml: 3,
                    display: 'block',
                    pl: 1.5,
                    fontSize: 12,
                    lineHeight: 1.2,
                    fontWeight: 'normal',
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                  }}
                >
                  {coverage.label}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, textAlign: 'right', mr: annualizedMr }}>
                <Typography
                  variant='body2'
                  sx={{
                    color: 'info.hint',
                    fontWeight: 'normal',
                    fontSize: 12,
                    lineHeight: 1.2,
                  }}
                >
                  {formatCurrency(coverage.ratedPremium ?? 0)}
                </Typography>
              </Box>
              {showDetailedTermColumns && <Box sx={{ flex: 1 }} />}
              {showDetailedTermColumns && (
                <Box sx={{ flex: 1, textAlign: 'right', mr: termMr }}>
                  <Typography
                    variant='body2'
                    sx={{
                      color: 'info.hint',
                      fontWeight: 'normal',
                      fontSize: 12,
                      lineHeight: 1.2,
                    }}
                  >
                    {formatCurrency(coverage.termPremium ?? 0)}
                  </Typography>
                </Box>
              )}
            </Box>
          ))}

          {/* Total Row */}
          <Box sx={rowStyles}>
            <Box sx={{ flex: 3 }}>
              <Typography
                variant='body2'
                sx={{
                  color: 'info.hint',
                  ml: 3,
                  fontWeight: 'bold',
                }}
              >
                Total
              </Typography>
            </Box>
            <Box sx={{ flex: 1, textAlign: 'right', mr: annualizedMr }}>
              <Typography
                variant='body2'
                sx={{
                  color: 'info.hint',
                  fontWeight: 'bold',
                }}
              >
                {formatCurrency(
                  (lastSuccessfulRating?.totalPremium ?? 0) + (lastSuccessfulRating?.totalTaxFeeSurcharge || 0),
                )}
              </Typography>
            </Box>
            {showDetailedTermColumns && <Box sx={{ flex: 1, mr: spacerColumnMr }} />}
            {showDetailedTermColumns && (
              <Box sx={{ flex: 1, textAlign: 'right', mr: termMr }}>
                <Typography
                  variant='body2'
                  sx={{
                    color: 'info.hint',
                    fontWeight: 'bold',
                  }}
                >
                  {formatCurrency(
                    (lastSuccessfulRating?.termPremium ?? 0) + (lastSuccessfulRating?.termTaxFeeSurcharge || 0),
                  )}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      open={isOpen}
      size='large'
      showBorders={false}
      title={headerText}
      handleCloseDialog={onCloseModal}
      sx={{
        maxWidth: modalType === RatingModalTypes.RatingReport ? 1000 : showDetailedTermColumns ? '100%' : '700px',
        margin: 'auto',
        '.MuiPaper-root':
          modalType === RatingModalTypes.RatingReport
            ? {
                height: 'fit-content',
                width: '100%',
              }
            : {},
      }}
      primaryBtnText='Close'
      primaryBtnAction={onCloseModal}
      tertiaryBtnAction={switchModalType}
      tertiaryBtnText={isInternalUser ? tertiaryButtonTxt : undefined}
      footerContent={modalType === RatingModalTypes.PremiumDetails && renderFooterContent()}
    >
      {isLoadingQuote || !quote || isLoadingQuoteItemCoverages ? (
        <Loading />
      ) : (
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          {modalType === RatingModalTypes.PremiumDetails ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                width: '100%',
                overflowY: 'auto',
                overflowX: 'hidden',
                minHeight: '300px',
                height: '100%',
              }}
            >
              <DataGrid
                hideFooter
                initialState={{ density: 'compact' }}
                columns={columns}
                rows={rows}
                rowCount={rows?.length ?? 0}
                columnHeaderHeight={showDetailedTermColumns ? 44 : 0}
                getRowHeight={() => 'auto'}
                sx={{
                  ...ratingSummaryDataGrid,
                  p: 0,
                  '& .MuiDataGrid-filler': {
                    '--DataGrid-rowBorderColor': 'transparent',
                  },
                }}
                slots={
                  showDetailedTermColumns
                    ? {}
                    : {
                        columnHeaders: EmptyColumnHeaders,
                      }
                }
              />
            </Box>
          ) : modalType === RatingModalTypes.RatingReport ? (
            <Box
              sx={{
                padding: '24px',
                height: '85%',
              }}
            >
              {ratingReport ? <RatingReportJsonViewer ratingReport={ratingReport} /> : <Loading />}
            </Box>
          ) : undefined}
        </Box>
      )}
    </Dialog>
  );
}
